import { useState } from 'react';
import _ from 'lodash';
import draftApi from '../../../../api/draft.api';
import { callApi } from '../../../../api/helpers/api.helpers';
import { omit } from '../services/constants';
import { uiSchemaProperties } from '../../../../shared/enums/uiSchemaProperties';
import { attachmentsFieldEventSuffix } from '../services/attachments.helper';

/**
 * Root attachments hook that manages the drop zone files and uploaded form attachments.
 *
 * @param submissionId id of the parent submission
 * @param attachmentsKey attachment form data field name
 * @param registry form registry
 * @param uiSchema attachments uiSchema
 * @param idSchema id schema used for the trigger post process event name
 * @returns drop zone files, handlers for adding and removing to drop zone and to uploaded files.
 */
export const useAttachmentsField = ({ submissionId, attachmentsKey, registry, uiSchema, idSchema }) => {
  const [dropZoneFiles, setDropZoneFiles] = useState({});
  const attachmentTypes = uiSchema[uiSchemaProperties.ATTACHMENTS_CATEGORIES] || [];
  const isSectionHidden = uiSchema[uiSchemaProperties.UI_HIDDEN];
  const showUserSpecificAttachments = uiSchema[uiSchemaProperties.ATTACHMENTS_USER_SPECIFIC];

  const shouldTriggerPostProcess = !!uiSchema?.[uiSchemaProperties.UI_OPTIONS]?.triggerPostProcess;

  const onFileDropped = (attachmentData, allowMultipleFiles) => {
    if (allowMultipleFiles) {
      setDropZoneFiles({ ...dropZoneFiles, [attachmentData.name]: attachmentData });
    } else {
      setDropZoneFiles({ [attachmentData.name]: attachmentData });
    }
  };

  const onDroppedFileRemoved = (originalFileName) => {
    setDropZoneFiles(omit(dropZoneFiles, originalFileName));
  };

  const onAttachmentUploaded = async (attachment) => {
    const attachmentsFormData = registry.getFormData(attachmentsKey) ?? [];
    await registry.setFormData([...attachmentsFormData, attachment], attachmentsKey);
    if (shouldTriggerPostProcess) {
      registry.triggerPostProcess(`${idSchema.$id}_add_${attachmentsFieldEventSuffix}`, attachment, false);
    }
  };

  const onUploadedAttachmentRemoved = async (attachment) => {
    await callApi(draftApi.deleteAttachment)(submissionId, attachment.id);
    const attachmentsFormData = registry.getFormData(attachmentsKey) ?? [];
    const updatedAttachments = attachmentsFormData.filter(a => a.id !== attachment.id);
    await registry.setFormData(updatedAttachments, attachmentsKey);
    if (shouldTriggerPostProcess) {
      registry.triggerPostProcess(`${idSchema.$id}_delete_${attachmentsFieldEventSuffix}`, attachment, false);
    }
  };

  const getCustomTooltipByProcedure = (showCustomTooltips) => {
    let tooltip = null;
    _.forEach(showCustomTooltips, (customToolTip) => {
      const _target = registry.getFormData(customToolTip.selector);
      if (_target === customToolTip?.value) {
        tooltip = customToolTip?.tooltip;
      }
    });
    return tooltip;
  };

  return {
    attachmentTypes,
    showUserSpecificAttachments,
    isSectionHidden,
    dropZoneFiles,
    onFileDropped,
    onDroppedFileRemoved,
    onAttachmentUploaded,
    onUploadedAttachmentRemoved,
    getCustomTooltipByProcedure,
  };
};
